/**
 * Vendors Page
 * Unified vendor management and comprehensive reporting
 */

import React, { useState, useEffect } from 'react'
import { Plus, Search, Filter, MoreVertical, Mail, Phone, DollarSign, FileText, Edit, Trash2, BarChart3, Download, Calendar, PieChart, Receipt, TrendingUp, FileBarChart, Users2 } from 'lucide-react'
import { format } from 'date-fns'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { AdminUser } from '@/lib/authService'
import { formatCurrency } from '@/lib/currency'
import VendorService, { Vendor, VendorSummary, VendorTransaction } from '@/services/vendorService'

interface VendorsProps {
  user: AdminUser | null
}

export default function Vendors({ user }: VendorsProps) {
  const [vendors, setVendors] = useState<VendorSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [reportLoading, setReportLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [showDialog, setShowDialog] = useState(false)
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null)
  const [selectedVendor, setSelectedVendor] = useState<VendorSummary | null>(null)
  const [transactions, setTransactions] = useState<VendorTransaction[]>([])
  const [showTransactions, setShowTransactions] = useState(false)

  // Report state
  const [statistics, setStatistics] = useState<any>(null)
  const [purchaseSummary, setPurchaseSummary] = useState<any[]>([])
  const [paymentHistory, setPaymentHistory] = useState<any[]>([])
  const [outstandingInvoices, setOutstandingInvoices] = useState<any[]>([])
  const [transactionReport, setTransactionReport] = useState<any[]>([])
  const [vendorByType, setVendorByType] = useState<any[]>([])

  // Report filters
  const [reportType, setReportType] = useState<string>('purchase_summary')
  const [reportVendor, setReportVendor] = useState<string>('all')
  const [startDate, setStartDate] = useState(
    format(new Date(new Date().setMonth(new Date().getMonth() - 1)), 'yyyy-MM-dd')
  )
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'))
  const [transactionType, setTransactionType] = useState<string>('all')

  useEffect(() => {
    if (user?.churchId) {
      loadVendors()
      loadStatistics()
    }
  }, [user, statusFilter, typeFilter])

  async function loadVendors() {
    if (!user?.churchId) return

    try {
      setLoading(true)
      const filters: any = {}

      if (statusFilter !== 'all') {
        filters.status = statusFilter
      }

      if (typeFilter !== 'all') {
        filters.vendor_type = typeFilter
      }

      if (searchTerm) {
        filters.search = searchTerm
      }

      const data = await VendorService.getVendors(user.churchId, filters)
      console.log('=== VENDORS LOADED ===')
      console.log('Number of vendors:', data.length)
      console.log('Vendor data:', data)
      data.forEach((vendor, index) => {
        console.log(`Vendor ${index + 1}:`, {
          code: vendor.vendor_code,
          name: vendor.vendor_name,
          current_balance: vendor.current_balance,
          current_balance_type: typeof vendor.current_balance,
          total_purchases: vendor.total_purchases,
          total_purchases_type: typeof vendor.total_purchases,
          total_transactions: vendor.total_transactions,
          raw_vendor: vendor
        })
      })

      // Check the aggregation
      const totalBalance = data.reduce((sum, v) => {
        console.log(`Adding balance: ${sum} + ${v.current_balance} (type: ${typeof v.current_balance})`)
        return sum + v.current_balance
      }, 0)
      console.log('Total balance calculated:', totalBalance)

      setVendors(data)
    } catch (error) {
      console.error('Error loading vendors:', error)
    } finally {
      setLoading(false)
    }
  }

  async function handleViewTransactions(vendor: VendorSummary) {
    setSelectedVendor(vendor)
    try {
      const data = await VendorService.getVendorTransactions(vendor.id)
      setTransactions(data)
      setShowTransactions(true)
    } catch (error) {
      console.error('Error loading transactions:', error)
    }
  }

  function handleAddVendor() {
    setEditingVendor(null)
    setShowDialog(true)
  }

  async function handleEditVendor(vendor: VendorSummary) {
    try {
      // Fetch the full vendor data from the vendors table (not the summary view)
      const fullVendor = await VendorService.getVendor(vendor.id)
      setEditingVendor(fullVendor)
      setShowDialog(true)
    } catch (error) {
      console.error('Error loading vendor:', error)
      alert('Failed to load vendor details')
    }
  }

  async function handleDeleteVendor(vendorId: string) {
    const userId = user?.userId || user?.id
    if (!userId || !confirm('Are you sure you want to delete this vendor?')) return

    try {
      await VendorService.deleteVendor(vendorId, userId)
      loadVendors()
    } catch (error) {
      console.error('Error deleting vendor:', error)
      alert('Failed to delete vendor')
    }
  }

  async function loadStatistics() {
    try {
      const stats = await VendorService.getVendorStatistics(user!.churchId)
      setStatistics(stats)

      const byType = await VendorService.getVendorByTypeSummary(user!.churchId)
      setVendorByType(byType)
    } catch (error) {
      console.error('Error loading statistics:', error)
    }
  }

  async function generateReport() {
    try {
      setReportLoading(true)

      const vendorId = reportVendor !== 'all' ? reportVendor : undefined

      switch (reportType) {
        case 'purchase_summary':
          const purchases = await VendorService.getVendorPurchaseSummary(
            user!.churchId,
            startDate,
            endDate,
            vendorId
          )
          setPurchaseSummary(purchases)
          break

        case 'payment_history':
          const payments = await VendorService.getVendorPaymentHistory(
            user!.churchId,
            startDate,
            endDate,
            vendorId
          )
          setPaymentHistory(payments)
          break

        case 'outstanding_invoices':
          const invoices = await VendorService.getVendorOutstandingInvoices(
            user!.churchId,
            vendorId
          )
          setOutstandingInvoices(invoices)
          break

        case 'transaction_report':
          const txType = transactionType !== 'all' ? transactionType : undefined
          const transactions = await VendorService.getVendorTransactionReport(
            user!.churchId,
            startDate,
            endDate,
            vendorId,
            txType
          )
          setTransactionReport(transactions)
          break

        default:
          break
      }
    } catch (error) {
      console.error('Error generating report:', error)
      alert('Failed to generate report')
    } finally {
      setReportLoading(false)
    }
  }

  const filteredVendors = vendors.filter(vendor =>
    searchTerm === '' ||
    vendor.vendor_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vendor.vendor_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vendor.email?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  function exportToCSV() {
    let csvContent = ''
    let filename = ''

    switch (reportType) {
      case 'purchase_summary':
        if (purchaseSummary.length === 0) {
          alert('No data to export. Please generate a report first.')
          return
        }
        filename = `vendor-purchase-summary-${startDate}-to-${endDate}.csv`
        csvContent = 'Vendor Code,Vendor Name,Type,Transactions,Total Purchases\n'
        purchaseSummary.forEach(item => {
          csvContent += `${item.vendor_code},"${item.vendor_name}","${item.vendor_type}",${item.transaction_count},${item.total_purchases}\n`
        })
        break

      case 'payment_history':
        if (paymentHistory.length === 0) {
          alert('No data to export. Please generate a report first.')
          return
        }
        filename = `vendor-payment-history-${startDate}-to-${endDate}.csv`
        csvContent = 'Date,Vendor Code,Vendor Name,Reference,Description,Amount,Balance\n'
        paymentHistory.forEach(payment => {
          csvContent += `${payment.transaction_date},${payment.vendors?.vendor_code},"${payment.vendors?.vendor_name}","${payment.reference_number || ''}","${payment.description || ''}",${payment.credit_amount},${payment.balance}\n`
        })
        break

      case 'outstanding_invoices':
        if (outstandingInvoices.length === 0) {
          alert('No data to export. Please generate a report first.')
          return
        }
        filename = `vendor-outstanding-invoices-${format(new Date(), 'yyyy-MM-dd')}.csv`
        csvContent = 'Date,Vendor Code,Vendor Name,Reference,Description,Amount\n'
        outstandingInvoices.forEach(invoice => {
          csvContent += `${invoice.expense_date},${invoice.vendors?.vendor_code},"${invoice.vendors?.vendor_name}","${invoice.reference_number || ''}","${invoice.description}",${invoice.amount}\n`
        })
        break

      case 'transaction_report':
        if (transactionReport.length === 0) {
          alert('No data to export. Please generate a report first.')
          return
        }
        filename = `vendor-transactions-${startDate}-to-${endDate}.csv`
        csvContent = 'Date,Vendor Code,Vendor Name,Type,Reference,Debit,Credit,Balance\n'
        transactionReport.forEach(txn => {
          csvContent += `${txn.transaction_date},${txn.vendors?.vendor_code},"${txn.vendors?.vendor_name}","${txn.transaction_type}","${txn.reference_number || ''}",${txn.debit_amount},${txn.credit_amount},${txn.balance}\n`
        })
        break

      default:
        alert('Please select a report type and generate a report first.')
        return
    }

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', filename)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b bg-background px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Vendors</h1>
            <p className="text-sm text-muted-foreground">
              Manage vendors and analyze spending patterns
            </p>
          </div>
          <Button onClick={handleAddVendor}>
            <Plus className="mr-2 h-4 w-4" />
            Add Vendor
          </Button>
        </div>
      </div>

      {/* Main Content - Unified Tabs */}
      <div className="flex-1 overflow-auto p-6">
        <Tabs defaultValue="management" className="space-y-4">
          <TabsList>
            <TabsTrigger value="management">
              <Users2 className="mr-2 h-4 w-4" />
              Vendor Management
            </TabsTrigger>
            <TabsTrigger value="reports">
              <BarChart3 className="mr-2 h-4 w-4" />
              Reports & Analytics
            </TabsTrigger>
          </TabsList>

          <TabsContent value="management" className="space-y-6 mt-0">
            {/* Filters */}
            <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search vendors..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>

              <div className="flex gap-2">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[150px]">
                    <Filter className="mr-2 h-4 w-4" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="supplier">Supplier</SelectItem>
                    <SelectItem value="contractor">Contractor</SelectItem>
                    <SelectItem value="service_provider">Service Provider</SelectItem>
                    <SelectItem value="utility">Utility</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="mb-6 grid gap-4 md:grid-cols-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Vendors</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{vendors.length}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Active Vendors</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {vendors.filter(v => v.status === 'active').length}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Outstanding Payables</CardTitle>
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {formatCurrency(vendors.reduce((sum, v) => sum + v.current_balance, 0), 'GHS')}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Purchases</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {formatCurrency(vendors.reduce((sum, v) => sum + v.total_purchases, 0), 'GHS')}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Vendors Table */}
            <Card>
          <CardHeader>
            <CardTitle>Vendor List</CardTitle>
            <CardDescription>
              {filteredVendors.length} vendors found
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Vendor Code</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                    <TableHead className="text-right">Purchases</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredVendors.map((vendor) => (
                    <TableRow key={vendor.id}>
                      <TableCell className="font-medium">{vendor.vendor_code}</TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{vendor.vendor_name}</div>
                          {vendor.contact_person && (
                            <div className="text-xs text-muted-foreground">
                              {vendor.contact_person}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="capitalize">{vendor.vendor_type.replace('_', ' ')}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          {vendor.email && (
                            <div className="flex items-center gap-1 text-xs">
                              <Mail className="h-3 w-3" />
                              {vendor.email}
                            </div>
                          )}
                          {vendor.phone && (
                            <div className="flex items-center gap-1 text-xs">
                              <Phone className="h-3 w-3" />
                              {vendor.phone}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {vendor.current_balance > 0 ? (
                          <span className="font-medium text-orange-600">
                            {formatCurrency(vendor.current_balance, 'GHS')}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(vendor.total_purchases, 'GHS')}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            vendor.status === 'active'
                              ? 'default'
                              : vendor.status === 'suspended'
                              ? 'destructive'
                              : 'secondary'
                          }
                        >
                          {vendor.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewTransactions(vendor)}>
                              <FileText className="mr-2 h-4 w-4" />
                              View Transactions
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleEditVendor(vendor)}>
                              <Edit className="mr-2 h-4 w-4" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDeleteVendor(vendor.id)}
                              className="text-destructive"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="reports" className="space-y-6 mt-0">
            {/* Report Statistics Cards */}
            {statistics && (
              <div className="grid gap-4 md:grid-cols-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Vendors</CardTitle>
                    <Users2 className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{statistics.total_vendors}</div>
                    <p className="text-xs text-muted-foreground">
                      {statistics.active_vendors} active
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Outstanding Balance</CardTitle>
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {formatCurrency(statistics.total_outstanding, 'GHS')}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {statistics.vendors_with_balance} vendors with balance
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Top Vendor</CardTitle>
                    <TrendingUp className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    {statistics.top_vendors_by_purchase[0] ? (
                      <>
                        <div className="text-lg font-bold">
                          {statistics.top_vendors_by_purchase[0].vendor_name}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {formatCurrency(statistics.top_vendors_by_purchase[0].total_purchases, 'GHS')}
                        </p>
                      </>
                    ) : (
                      <div className="text-sm text-muted-foreground">No data</div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Vendor Types</CardTitle>
                    <PieChart className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{vendorByType.length}</div>
                    <p className="text-xs text-muted-foreground">
                      Different categories
                    </p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Report Filters */}
            <Card>
              <CardHeader>
                <CardTitle>Generate Report</CardTitle>
                <CardDescription>Select report type and filters</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  <div className="space-y-2">
                    <Label htmlFor="reportType">Report Type</Label>
                    <Select value={reportType} onValueChange={setReportType}>
                      <SelectTrigger id="reportType">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="purchase_summary">Purchase Summary</SelectItem>
                        <SelectItem value="payment_history">Payment History</SelectItem>
                        <SelectItem value="outstanding_invoices">Outstanding Invoices</SelectItem>
                        <SelectItem value="transaction_report">Transaction Report</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="reportVendor">Vendor</Label>
                    <Select value={reportVendor} onValueChange={setReportVendor}>
                      <SelectTrigger id="reportVendor">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Vendors</SelectItem>
                        {vendors.map((vendor) => (
                          <SelectItem key={vendor.id} value={vendor.id}>
                            {vendor.vendor_code} - {vendor.vendor_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {reportType !== 'outstanding_invoices' && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="startDate">Start Date</Label>
                        <Input
                          id="startDate"
                          type="date"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="endDate">End Date</Label>
                        <Input
                          id="endDate"
                          type="date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                        />
                      </div>
                    </>
                  )}

                  {reportType === 'transaction_report' && (
                    <div className="space-y-2">
                      <Label htmlFor="transactionType">Transaction Type</Label>
                      <Select value={transactionType} onValueChange={setTransactionType}>
                        <SelectTrigger id="transactionType">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Types</SelectItem>
                          <SelectItem value="purchase">Purchase</SelectItem>
                          <SelectItem value="payment">Payment</SelectItem>
                          <SelectItem value="credit_note">Credit Note</SelectItem>
                          <SelectItem value="debit_note">Debit Note</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button onClick={generateReport} disabled={reportLoading}>
                    <BarChart3 className="mr-2 h-4 w-4" />
                    {reportLoading ? 'Generating...' : 'Generate Report'}
                  </Button>
                  <Button variant="outline" onClick={exportToCSV}>
                    <Download className="mr-2 h-4 w-4" />
                    Export
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Report Results */}
            {reportType === 'purchase_summary' && purchaseSummary.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Purchase Summary Report</CardTitle>
                  <CardDescription>
                    Vendor purchases from {new Date(startDate).toLocaleDateString()} to {new Date(endDate).toLocaleDateString()}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Vendor Code</TableHead>
                        <TableHead>Vendor Name</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="text-right">Transactions</TableHead>
                        <TableHead className="text-right">Total Purchases</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {purchaseSummary.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium">{item.vendor_code}</TableCell>
                          <TableCell>{item.vendor_name}</TableCell>
                          <TableCell className="capitalize">{item.vendor_type?.replace('_', ' ')}</TableCell>
                          <TableCell className="text-right">{item.transaction_count}</TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(item.total_purchases, 'GHS')}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-muted/50">
                        <TableCell colSpan={3} className="font-bold">Total</TableCell>
                        <TableCell className="text-right font-bold">
                          {purchaseSummary.reduce((sum, item) => sum + item.transaction_count, 0)}
                        </TableCell>
                        <TableCell className="text-right font-bold">
                          {formatCurrency(purchaseSummary.reduce((sum, item) => sum + item.total_purchases, 0), 'GHS')}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            {reportType === 'payment_history' && paymentHistory.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Payment History Report</CardTitle>
                  <CardDescription>
                    Vendor payments from {new Date(startDate).toLocaleDateString()} to {new Date(endDate).toLocaleDateString()}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Vendor</TableHead>
                        <TableHead>Reference</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead className="text-right">Balance</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paymentHistory.map((payment) => (
                        <TableRow key={payment.id}>
                          <TableCell>{new Date(payment.transaction_date).toLocaleDateString()}</TableCell>
                          <TableCell>
                            {payment.vendors?.vendor_code} - {payment.vendors?.vendor_name}
                          </TableCell>
                          <TableCell>{payment.reference_number || '-'}</TableCell>
                          <TableCell className="max-w-xs truncate">{payment.description || '-'}</TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(payment.credit_amount, 'GHS')}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(payment.balance, 'GHS')}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-muted/50">
                        <TableCell colSpan={4} className="font-bold">Total Payments</TableCell>
                        <TableCell className="text-right font-bold">
                          {formatCurrency(paymentHistory.reduce((sum, p) => sum + p.credit_amount, 0), 'GHS')}
                        </TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            {reportType === 'outstanding_invoices' && outstandingInvoices.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Outstanding Invoices Report</CardTitle>
                  <CardDescription>
                    Unpaid vendor invoices as of {new Date().toLocaleDateString()}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Vendor</TableHead>
                        <TableHead>Reference</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {outstandingInvoices.map((invoice) => (
                        <TableRow key={invoice.id}>
                          <TableCell>{new Date(invoice.expense_date).toLocaleDateString()}</TableCell>
                          <TableCell>
                            {invoice.vendors?.vendor_code} - {invoice.vendors?.vendor_name}
                          </TableCell>
                          <TableCell>{invoice.reference_number || '-'}</TableCell>
                          <TableCell className="max-w-xs truncate">{invoice.description}</TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(invoice.amount, 'GHS')}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-muted/50">
                        <TableCell colSpan={4} className="font-bold">Total Outstanding</TableCell>
                        <TableCell className="text-right font-bold">
                          {formatCurrency(outstandingInvoices.reduce((sum, i) => sum + i.amount, 0), 'GHS')}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            {reportType === 'transaction_report' && transactionReport.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Transaction Detail Report</CardTitle>
                  <CardDescription>
                    All vendor transactions from {new Date(startDate).toLocaleDateString()} to {new Date(endDate).toLocaleDateString()}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Vendor</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Reference</TableHead>
                        <TableHead className="text-right">Debit</TableHead>
                        <TableHead className="text-right">Credit</TableHead>
                        <TableHead className="text-right">Balance</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {transactionReport.map((txn) => (
                        <TableRow key={txn.id}>
                          <TableCell>{new Date(txn.transaction_date).toLocaleDateString()}</TableCell>
                          <TableCell>
                            {txn.vendors?.vendor_code} - {txn.vendors?.vendor_name}
                          </TableCell>
                          <TableCell className="capitalize">{txn.transaction_type.replace('_', ' ')}</TableCell>
                          <TableCell>{txn.reference_number || '-'}</TableCell>
                          <TableCell className="text-right">
                            {txn.debit_amount > 0 ? formatCurrency(txn.debit_amount, 'GHS') : '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            {txn.credit_amount > 0 ? formatCurrency(txn.credit_amount, 'GHS') : '-'}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(txn.balance, 'GHS')}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            {/* Vendor by Type Summary */}
            {vendorByType.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Vendors by Type Summary</CardTitle>
                  <CardDescription>Breakdown of vendors and purchases by category</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Vendor Type</TableHead>
                        <TableHead className="text-right">Count</TableHead>
                        <TableHead className="text-right">Total Purchases</TableHead>
                        <TableHead className="text-right">Outstanding</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {vendorByType.map((type, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium capitalize">
                            {type.vendor_type.replace('_', ' ')}
                          </TableCell>
                          <TableCell className="text-right">{type.vendor_count}</TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(type.total_purchases, 'GHS')}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(type.total_outstanding, 'GHS')}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-muted/50">
                        <TableCell className="font-bold">Total</TableCell>
                        <TableCell className="text-right font-bold">
                          {vendorByType.reduce((sum, t) => sum + t.vendor_count, 0)}
                        </TableCell>
                        <TableCell className="text-right font-bold">
                          {formatCurrency(vendorByType.reduce((sum, t) => sum + t.total_purchases, 0), 'GHS')}
                        </TableCell>
                        <TableCell className="text-right font-bold">
                          {formatCurrency(vendorByType.reduce((sum, t) => sum + t.total_outstanding, 0), 'GHS')}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Vendor Transactions Dialog */}
      <Dialog open={showTransactions} onOpenChange={setShowTransactions}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Vendor Transactions - {selectedVendor?.vendor_name}</DialogTitle>
            <DialogDescription>
              Transaction history for {selectedVendor?.vendor_code}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-96 overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead className="text-right">Debit</TableHead>
                  <TableHead className="text-right">Credit</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.map((txn) => (
                  <TableRow key={txn.id}>
                    <TableCell>{new Date(txn.transaction_date).toLocaleDateString()}</TableCell>
                    <TableCell className="capitalize">{txn.transaction_type.replace('_', ' ')}</TableCell>
                    <TableCell>{txn.reference_number || '-'}</TableCell>
                    <TableCell className="text-right">
                      {txn.debit_amount > 0 ? formatCurrency(txn.debit_amount, 'GHS') : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      {txn.credit_amount > 0 ? formatCurrency(txn.credit_amount, 'GHS') : '-'}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(txn.balance, 'GHS')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Vendor Dialog */}
      <VendorFormDialog
        open={showDialog}
        onOpenChange={setShowDialog}
        vendor={editingVendor}
        onSave={loadVendors}
        user={user}
      />
    </div>
  )
}

// Vendor Form Dialog Component
interface VendorFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  vendor: Vendor | null
  onSave: () => void
  user: AdminUser | null
}

function VendorFormDialog({ open, onOpenChange, vendor, onSave, user }: VendorFormDialogProps) {
  const [formData, setFormData] = useState<any>({
    vendor_name: '',
    vendor_type: 'supplier',
    contact_person: '',
    email: '',
    phone: '',
    mobile: '',
    website: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state_province: '',
    postal_code: '',
    country: 'Ghana',
    tax_id: '',
    payment_terms: 30,
    credit_limit: 0,
    bank_name: '',
    bank_account_number: '',
    bank_branch: '',
    status: 'active',
    is_active: true,
    notes: ''
  })

  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (vendor) {
      setFormData(vendor)
    } else {
      setFormData({
        vendor_name: '',
        vendor_type: 'supplier',
        contact_person: '',
        email: '',
        phone: '',
        mobile: '',
        website: '',
        address_line1: '',
        address_line2: '',
        city: '',
        state_province: '',
        postal_code: '',
        country: 'Ghana',
        tax_id: '',
        payment_terms: 30,
        credit_limit: 0,
        bank_name: '',
        bank_account_number: '',
        bank_branch: '',
        status: 'active',
        is_active: true,
        notes: ''
      })
    }
  }, [vendor, open])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    console.log('=== handleSubmit called ===')
    console.log('User:', user)
    console.log('Form Data:', formData)
    console.log('Vendor (editing):', vendor)

    const userId = user?.userId || user?.id
    if (!user?.churchId || !userId) {
      console.error('Missing user data:', { churchId: user?.churchId, userId })
      alert('Missing user information. Please log in again.')
      return
    }

    try {
      setSaving(true)
      console.log('Starting save operation...')

      if (vendor) {
        console.log('Updating vendor:', vendor.id)
        await VendorService.updateVendor(vendor.id, formData, userId)
      } else {
        console.log('Creating new vendor with churchId:', user.churchId)
        const result = await VendorService.createVendor(user.churchId, formData, userId)
        console.log('Create result:', result)
      }

      console.log('Save successful!')
      onSave()
      onOpenChange(false)
    } catch (error) {
      console.error('Error saving vendor:', error)
      console.error('Error details:', JSON.stringify(error, null, 2))
      alert('Failed to save vendor: ' + (error instanceof Error ? error.message : String(error)))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{vendor ? 'Edit Vendor' : 'Add New Vendor'}</DialogTitle>
          <DialogDescription>
            {vendor ? 'Update vendor information' : 'Enter vendor details'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="contact">Contact & Address</TabsTrigger>
              <TabsTrigger value="financial">Financial</TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="vendor_name">Vendor Name *</Label>
                  <Input
                    id="vendor_name"
                    value={formData.vendor_name}
                    onChange={(e) => setFormData({ ...formData, vendor_name: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="vendor_type">Vendor Type *</Label>
                  <Select
                    value={formData.vendor_type}
                    onValueChange={(value) => setFormData({ ...formData, vendor_type: value })}
                  >
                    <SelectTrigger id="vendor_type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="supplier">Supplier</SelectItem>
                      <SelectItem value="contractor">Contractor</SelectItem>
                      <SelectItem value="service_provider">Service Provider</SelectItem>
                      <SelectItem value="utility">Utility</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tax_id">Tax ID / TIN</Label>
                  <Input
                    id="tax_id"
                    value={formData.tax_id}
                    onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData({ ...formData, status: value })}
                  >
                    <SelectTrigger id="status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Input
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="contact" className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="contact_person">Contact Person</Label>
                  <Input
                    id="contact_person"
                    value={formData.contact_person}
                    onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mobile">Mobile</Label>
                  <Input
                    id="mobile"
                    value={formData.mobile}
                    onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="address_line1">Address Line 1</Label>
                  <Input
                    id="address_line1"
                    value={formData.address_line1}
                    onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="address_line2">Address Line 2</Label>
                  <Input
                    id="address_line2"
                    value={formData.address_line2}
                    onChange={(e) => setFormData({ ...formData, address_line2: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="state_province">State/Province</Label>
                  <Input
                    id="state_province"
                    value={formData.state_province}
                    onChange={(e) => setFormData({ ...formData, state_province: e.target.value })}
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="financial" className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="payment_terms">Payment Terms (Days)</Label>
                  <Input
                    id="payment_terms"
                    type="number"
                    value={formData.payment_terms}
                    onChange={(e) => setFormData({ ...formData, payment_terms: parseInt(e.target.value) || 0 })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="credit_limit">Credit Limit</Label>
                  <Input
                    id="credit_limit"
                    type="number"
                    step="0.01"
                    value={formData.credit_limit}
                    onChange={(e) => setFormData({ ...formData, credit_limit: parseFloat(e.target.value) || 0 })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bank_name">Bank Name</Label>
                  <Input
                    id="bank_name"
                    value={formData.bank_name}
                    onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bank_account_number">Bank Account Number</Label>
                  <Input
                    id="bank_account_number"
                    value={formData.bank_account_number}
                    onChange={(e) => setFormData({ ...formData, bank_account_number: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bank_branch">Bank Branch</Label>
                  <Input
                    id="bank_branch"
                    value={formData.bank_branch}
                    onChange={(e) => setFormData({ ...formData, bank_branch: e.target.value })}
                  />
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving...' : vendor ? 'Update Vendor' : 'Create Vendor'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
